import Checkbox from 'components/Form/Checkbox';
import CheckboxRadioGroup from 'components/Form/CheckboxRadioGroup';
import Dropdown from 'components/Form/Dropdown';
import Field from 'components/Form/Field';
import FieldDivider from 'components/Form/FieldDivider';
import FormActions from 'components/Form/FormActions';
import FormResult from 'components/Form/FormResult';
import InputIncremental from 'components/Form/InputIncremental';
import InputTextInsertVariables from 'components/Form/InputTextInsertVariables';
import SubmitButton from 'components/Form/SubmitButton';
import TextAreaInsertVariables from 'components/Form/TextAreaInsertVariables';
import {RequiredType} from 'components/Form/types';
import Layout from 'components/Layout';
import Link from 'components/Link';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import useBeforeUnload from 'hooks/useBeforeUnload';
import lodash from 'lodash';
import {FC, useMemo} from 'react';
import {FormProvider, RegisterOptions, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {c, jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiFormError} from 'utils/request/result';

import {sendingSettingsLinks} from '../../utils';
import {
    UpdateDocumentReminderSettingsRequest,
    useUpdateDocumentReminderSettings,
} from '../services';
import {DocumentReminderSettingsEditBackendData, FormFields} from './types';
import getInitialFormField from './utils';

const DocumentReminderSettingsEditPage: FC = () => {
    const {data} = useBackend<DocumentReminderSettingsEditBackendData>();

    const initialData = useMemo(() => getInitialFormField(data), [data]);
    const form = useForm<FormFields>({
        defaultValues: initialData,
        mode: 'onBlur',
    });
    const {isSubmitting} = form.formState;

    const {isPending, mutateAsync} = useUpdateDocumentReminderSettings();
    const formData = form.watch();

    const {allowPageChange} = useBeforeUnload(
        !lodash.isEqual(initialData, formData),
    );

    const handleSubmit = async () => {
        const request: UpdateDocumentReminderSettingsRequest = {
            attachmentTypes: [...formData.attachmentTypes],
            isActive: formData.isActive,
            secureSendMessageEn: formData.secureSendMessageEn,
            secureSendMessageJa: formData.secureSendMessageJa,
            secureSendSubjectEn: formData.secureSendSubjectEn,
            secureSendSubjectJa: formData.secureSendSubjectJa,
            timing: formData.timing,
        };

        await mutateAsync(request)
            .then(() => {
                allowPageChange();
                setUrl(window.Urls.partner_sending_settings_view());
            })
            .catch(onApiFormError(form));
    };

    const documentReminderHelpCenterLink = (
        <Link
            className="font-semibold"
            href={sendingSettingsLinks.documentReminderHelpLink}
            isOpenInNewTab={true}
            key="document-reminder-help-link"
        >{t`Help Center`}</Link>
    );

    const attachmentTypeDropdownOptions = [
        {label: c('dropdown').t`Order`, value: 'order'},
        {label: c('dropdown').t`Quote`, value: 'quote'},
        {label: c('dropdown').t`Order Slip`, value: 'orderslip'},
        {
            label: c('dropdown').t`Order Confirmation`,
            value: 'orderconfirmation',
        },
        {label: c('dropdown').t`Timesheet`, value: 'timesheet'},
        {label: c('dropdown').t`Delivery Slip`, value: 'deliveryslip'},
        {label: c('dropdown').t`Acceptance Certificate`, value: 'acceptance'},
        {label: c('dropdown').t`Payment Notice`, value: 'paymentnotice'},
        {label: c('dropdown').t`Invoice`, value: 'invoice'},
        {label: c('dropdown').t`Receipt`, value: 'receipt'},
        {label: c('dropdown').t`ZIP`, value: 'zip'},
        {label: c('dropdown').t`PDF`, value: 'pdf'},
    ];

    const commonRuleProps: {
        requiredType: RequiredType;
        rules: RegisterOptions;
    } = {
        requiredType: 'hidden',
        rules: {
            required: true,
        },
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
                <FormResult className="mb-4" />
                <NavigationTabs.Partner selection="sending_settings" />
                <div>
                    <SectionHeader
                        button={{
                            disabled: isSubmitting,
                            isLoading: isPending,
                            kind: 'primary',
                            label: t`Save Settings`,
                            type: 'submit',
                        }}
                        subtitle={jt`Edit the settings of your Document Reminders. See the ${documentReminderHelpCenterLink} (Japanese site) for more information.`}
                        title={t`Document Reminder`}
                    />
                    <Layout.SideBySide
                        className="mt-4"
                        classNameLabel="w-[14rem] max-w-[14rem]"
                    >
                        <Field label={t`Document Reminder`} type="checkbox">
                            <CheckboxRadioGroup>
                                <Checkbox className="my-1" name="isActive" />
                            </CheckboxRadioGroup>
                        </Field>
                        <FieldDivider>{t`General Settings`}</FieldDivider>
                        <InputIncremental
                            helpText={t`Number of days before checking unviewed status and sending a reminder email.`}
                            label={t`Reminder Timing`}
                            max={50}
                            min={1}
                            name="timing"
                            suffix={t`Days Later`}
                            {...commonRuleProps}
                        />
                        <Dropdown
                            isMultiChoices={true}
                            label={t`Attachment Types`}
                            name="attachmentTypes"
                            options={attachmentTypeDropdownOptions}
                            placeholder={t`Select attachment type(s)`}
                            type="select"
                            {...commonRuleProps}
                        />
                        <FieldDivider>{t`Secure Send Reminder Email Settings`}</FieldDivider>
                        <InputTextInsertVariables
                            label={t`Japanese Reminder Email Subject`}
                            name="secureSendSubjectJa"
                            options={data.insertableVariables}
                            {...commonRuleProps}
                        />
                        <TextAreaInsertVariables
                            label={t`Japanese Reminder Email Message`}
                            name="secureSendMessageJa"
                            options={data.insertableVariables}
                            resize="auto"
                            rows={4}
                            {...commonRuleProps}
                        />
                        <Field
                            helpText={[
                                t`The above text will be appended to the message.`,
                                t`The recipient does not have to sign up to MakeLeaps to download the PDF.`,
                            ].join('\n')}
                            label=""
                            type="value"
                        >
                            <pre className="whitespace-pre-wrap break-words rounded border border-grey-300 bg-grey-100 p-2">
                                {data.secureSendMessageSuffixJa}
                            </pre>
                        </Field>
                        <InputTextInsertVariables
                            label={t`English Reminder Email Subject`}
                            name="secureSendSubjectEn"
                            options={data.insertableVariables}
                            {...commonRuleProps}
                        />
                        <TextAreaInsertVariables
                            className="flex"
                            label={t`English Reminder Email Message`}
                            name="secureSendMessageEn"
                            options={data.insertableVariables}
                            resize="auto"
                            rows={4}
                            {...commonRuleProps}
                        />
                        <Field
                            helpText={[
                                t`The above text will be appended to the message.`,
                                t`The recipient does not have to sign up to MakeLeaps to download the PDF.`,
                            ].join('\n')}
                            label=""
                            type="value"
                        >
                            <pre className="whitespace-pre-wrap break-words rounded border border-grey-300 bg-grey-100 p-2">
                                {data.secureSendMessageSuffixEn}
                            </pre>
                        </Field>
                        <FieldDivider>{t`Client Inbox Reminder Email Settings`}</FieldDivider>
                        <div aria-label="spacer" role="gridcell">
                            &nbsp;
                        </div>
                        <div
                            className="text-sm text-light-secondary"
                            role="gridcell"
                        >
                            {jt`Currently, Client Inbox reminders are not customizable. For more information, see the ${documentReminderHelpCenterLink} (Japanese site).`}
                        </div>
                        <FormActions align="left">
                            <SubmitButton
                                disabled={isSubmitting}
                                isLoading={isPending}
                            >
                                {t`Save Settings`}
                            </SubmitButton>
                            <LinkButton
                                href={window.Urls.partner_sending_settings_view()}
                            >{t`Cancel`}</LinkButton>
                        </FormActions>
                    </Layout.SideBySide>
                </div>
            </form>
        </FormProvider>
    );
};

export default DocumentReminderSettingsEditPage;
